import {
  ServiceInterface,
  SuggestedService,
  SuggestedServiceKind
} from 'types';

import { isApiFlagEnabled } from './book';

const TYPE_LABEL_TO_CODE: Record<string, SuggestedServiceKind> = {
  p: 'P',
  s: 'S',
  e: 'E',
  product: 'P',
  service: 'S',
  class: 'E',
  'master service': 'S',
  'master class': 'E'
};

export const buildSuggestedServicesFetchKey = (
  locationId: number,
  serviceIds: number[]
): string => {
  const uniqueIds = Array.from(new Set(serviceIds.filter(Boolean))).sort(
    (a, b) => a - b
  );

  return `v2:${locationId}:${uniqueIds.join(',')}`;
};

const isSuggestedServiceRow = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const row = value as Record<string, unknown>;
  return (
    row.item_id !== undefined ||
    row.rec_id !== undefined ||
    row.svc_id !== undefined ||
    row.svcid !== undefined ||
    row.item_name !== undefined ||
    row.name !== undefined
  );
};

const expandCandidateValue = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (isSuggestedServiceRow(value)) {
    return [value];
  }

  return [];
};

const resolveTypeFromLabel = (label: string): SuggestedServiceKind | null => {
  if (label in TYPE_LABEL_TO_CODE) {
    return TYPE_LABEL_TO_CODE[label];
  }

  if (label.includes('product')) {
    return 'P';
  }

  if (label.includes('class')) {
    return 'E';
  }

  if (label.includes('service')) {
    return 'S';
  }

  return null;
};

export const dedupeSuggestedServices = (
  suggestions: SuggestedService[]
): SuggestedService[] => {
  const seen = new Set<number>();

  return suggestions.filter((item) => {
    const key = item.rec_id ?? item.item_id;
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

export const getVisibleSuggestedServices = (
  suggestions: SuggestedService[]
): SuggestedService[] =>
  dedupeSuggestedServices(
    suggestions.filter(
      (item) => item.kind === 'S' || item.kind === 'E' || item.kind === 'P'
    )
  );

export const extractSuggestedServiceRows = (data: unknown): unknown[] => {
  if (Array.isArray(data)) {
    return data;
  }

  if (!data || typeof data !== 'object') {
    return [];
  }

  const record = data as Record<string, unknown>;
  const candidates = [
    record.results,
    record.data,
    record.items,
    record.suggestions,
    record.list,
    record.svc_arr,
    record.suggested_svc
  ];

  const matchedRows = candidates.reduce<unknown[] | null>(
    (found, candidate) => {
      if (found) {
        return found;
      }

      if (Array.isArray(candidate)) {
        return candidate;
      }

      if (candidate && typeof candidate === 'object') {
        if (isSuggestedServiceRow(candidate)) {
          return [candidate];
        }

        return Object.values(candidate as Record<string, unknown>).flatMap(
          expandCandidateValue
        );
      }

      return null;
    },
    null
  );

  if (matchedRows) {
    return matchedRows;
  }

  const rowValues = Object.values(record).filter(isSuggestedServiceRow);
  if (rowValues.length) {
    return rowValues;
  }

  return [];
};

export const normalizeSuggestedServiceKind = (
  typeCode: unknown,
  typeLabel?: unknown
): SuggestedServiceKind | null => {
  const label = String(typeLabel ?? '')
    .trim()
    .toLowerCase();

  const fromLabel = resolveTypeFromLabel(label);
  if (fromLabel) {
    return fromLabel;
  }

  const code = String(typeCode ?? '')
    .trim()
    .toLowerCase();

  if (code === 'p' || code === 's' || code === 'e') {
    return code.toUpperCase() as SuggestedServiceKind;
  }

  // Korvue uses type_code "U" for bookable classes.
  if (code === 'u') {
    return 'E';
  }

  return null;
};

const readFirstPositiveNumber = (candidates: unknown[]): number => {
  const match = candidates.find((candidate) => {
    const value = Number(candidate);
    return Number.isFinite(value) && value > 0;
  });

  return match !== undefined ? Number(match) : 0;
};

const readFirstFiniteNumber = (candidates: unknown[]): number | undefined => {
  const match = candidates.find((candidate) =>
    Number.isFinite(Number(candidate))
  );

  return match !== undefined ? Number(match) : undefined;
};

const readFirstNonEmptyString = (candidates: unknown[]): string => {
  const match = candidates.find((candidate) => String(candidate ?? '').trim());

  return match !== undefined ? String(match).trim() : '';
};

const readSuggestedServiceId = (row: Record<string, unknown>): number =>
  readFirstPositiveNumber([
    row.item_id,
    row.svc_id,
    row.svcid,
    row.id,
    row.itemid
  ]);

const readSuggestedServiceName = (row: Record<string, unknown>): string =>
  readFirstNonEmptyString([
    row.item_name,
    row.name,
    row.svc_name,
    row.title,
    row.dscr_line
  ]);

const readSuggestedRetail = (
  row: Record<string, unknown>
): number | undefined =>
  readFirstFiniteNumber([row.svc_price, row.retail, row.price]);

const readParentSvcId = (row: Record<string, unknown>): number =>
  readFirstPositiveNumber([
    row.parent_svc_id,
    row.parent_svcid,
    row.source_svc_id
  ]);

const readSuggestedDscr = (
  row: Record<string, unknown>
): string | undefined => {
  if (row.dscr !== undefined && row.dscr !== null) {
    return String(row.dscr);
  }

  if (row.description !== undefined && row.description !== null) {
    return String(row.description);
  }

  return undefined;
};

const isBookableSuggestedService = (
  record: Record<string, unknown>
): boolean => {
  if (record.allow_book === undefined || record.allow_book === null) {
    return true;
  }

  return isApiFlagEnabled(record.allow_book);
};

export const normalizeSuggestedServices = (
  data: unknown
): SuggestedService[] => {
  const rows = extractSuggestedServiceRows(data);
  const suggestions: SuggestedService[] = [];

  rows.forEach((row) => {
    if (!row || typeof row !== 'object') {
      return;
    }

    const record = row as Record<string, unknown>;

    if (!isBookableSuggestedService(record)) {
      return;
    }

    const typeCode = record.type_code ?? record.ps_type;
    const typeLabel = record.type ?? record.ps_type;
    const kind = normalizeSuggestedServiceKind(typeCode, typeLabel);
    const item_id = readSuggestedServiceId(record);
    const item_name = readSuggestedServiceName(record);
    const parent_svc_id = readParentSvcId(record);

    if (!kind || !item_id || !item_name) {
      return;
    }

    const resolvedTypeCode = String(typeCode ?? kind);
    const resolvedTypeLabel = String(typeLabel ?? kind);
    const retail = readSuggestedRetail(record);
    const dscr = readSuggestedDscr(record);

    suggestions.push({
      rec_id: Number(record.rec_id ?? item_id),
      type_code: resolvedTypeCode,
      type: resolvedTypeLabel,
      item_id,
      item_name,
      parent_svc_id,
      typid:
        record.typid !== undefined && record.typid !== null
          ? Number(record.typid)
          : undefined,
      allow_book:
        record.allow_book !== undefined && record.allow_book !== null
          ? Number(record.allow_book)
          : undefined,
      is_master:
        record.is_master !== undefined && record.is_master !== null
          ? Number(record.is_master)
          : undefined,
      ...(record.duration !== undefined && record.duration !== null
        ? { duration: String(record.duration) }
        : {}),
      ...(dscr ? { dscr } : {}),
      ...(retail !== undefined ? { retail, svc_price: retail } : {}),
      ...(record.online_booking_meth !== undefined &&
      record.online_booking_meth !== null
        ? { online_booking_meth: Number(record.online_booking_meth) }
        : {}),
      kind
    });
  });

  return dedupeSuggestedServices(suggestions);
};

export const getSuggestedServiceKey = (
  suggestion: SuggestedService,
  index: number
): string => `suggested-${suggestion.rec_id}-${index}`;

export const hasSuggestedServices = (
  suggestions: SuggestedService[]
): boolean => suggestions.length > 0;

export type SuggestedServiceGroup = {
  serviceId: number;
  serviceName: string;
  suggestions: SuggestedService[];
};

export const groupSuggestedServicesBySelection = (
  selectedServices: ServiceInterface[],
  suggestions: SuggestedService[]
): SuggestedServiceGroup[] => {
  const visible = getVisibleSuggestedServices(suggestions);
  if (!visible.length) {
    return [];
  }

  const selectedById = new Map(
    selectedServices.map((service) => [service.id, service])
  );
  const grouped = new Map<number, SuggestedService[]>();

  visible.forEach((item) => {
    const parentId = item.parent_svc_id;
    const list = grouped.get(parentId) ?? [];
    list.push(item);
    grouped.set(parentId, list);
  });

  const matchedGroups = Array.from(grouped.entries())
    .filter(([parentId]) => selectedById.has(parentId))
    .map(([parentId, items]) => ({
      serviceId: parentId,
      serviceName: selectedById.get(parentId)!.name,
      suggestions: items
    }));

  if (matchedGroups.length) {
    return matchedGroups;
  }

  if (selectedServices.length === 1) {
    return [
      {
        serviceId: selectedServices[0].id,
        serviceName: selectedServices[0].name,
        suggestions: visible
      }
    ];
  }

  return Array.from(grouped.entries()).map(([parentId, items]) => ({
    serviceId: parentId,
    serviceName:
      selectedById.get(parentId)?.name ??
      selectedServices.find((service) => service.id === parentId)?.name ??
      'Your selection',
    suggestions: items
  }));
};

export const hasVisibleSuggestedServiceGroups = (
  selectedServices: ServiceInterface[],
  suggestions: SuggestedService[]
): boolean =>
  groupSuggestedServicesBySelection(selectedServices, suggestions).length > 0;

/** Show the step when the API returned bookable suggestions (skip only when empty). */
export const shouldShowSuggestedServicesStep = (
  _selectedServices: ServiceInterface[],
  suggestions: SuggestedService[]
): boolean => hasSuggestedServices(suggestions);

export const SUGGESTED_SERVICES_STEP_TITLE = 'Something to Consider';

export const SUGGESTED_SERVICES_STEP_DESCRIPTION =
  'Based on your booking, our professionals recommend the following';

export const SUGGESTED_SERVICES_GROUP_TITLE = 'Recommended for you';
